import { z } from 'zod';
import { idempotency, toJobFields } from './spine';
import { listInput } from './list';

/** Work Orders preserve the existing execution job ID, public links and offline keys. */
export const workOrderIdInput = z.object({ workOrderId: z.string().uuid() }).strict();
export type WorkOrderIdInput = z.infer<typeof workOrderIdInput>;
export const workOrderListInput = listInput
  .extend({ commercialJobId: z.string().uuid().optional() })
  .strict();
export type WorkOrderListInput = z.infer<typeof workOrderListInput>;
/** Only an authorized office action confirms scope; payment is a separate fact. */
export const workOrderConfirmationPlanInput = z
  .object({ quoteId: z.string().uuid() })
  .strict();
export type WorkOrderConfirmationPlanInput = z.infer<
  typeof workOrderConfirmationPlanInput
>;
const resourceResolution = z
  .object({
    quoteLineId: z.string().uuid(),
    priceBookItemId: z.string().uuid().optional(),
    sourceLocationId: z.string().uuid().optional(),
    assignedUserId: z.string().uuid().nullable().optional(),
    acknowledgeRetiredItem: z.boolean().optional(),
    reason: z.string().trim().min(1).max(500),
  })
  .strict();
export const workOrderConfirmInput = idempotency
  .extend({
    quoteId: z.string().uuid(),
    ...toJobFields,
    propertyId: z.string().uuid().optional(),
    propertyResolutionReason: z.string().trim().min(1).max(500).optional(),
    resourceResolutions: z.array(resourceResolution).max(200).optional(),
  })
  .strict()
  .refine((v) => !v.propertyId || Boolean(v.propertyResolutionReason), {
    message: 'Record why the execution address is being supplied',
    path: ['propertyResolutionReason'],
  })
  .refine(
    (v) =>
      new Set(v.resourceResolutions?.map((r) => r.quoteLineId)).size ===
      (v.resourceResolutions?.length ?? 0),
    { message: 'Resolve each Quote line once', path: ['resourceResolutions'] },
  );
export type WorkOrderConfirmInput = z.infer<typeof workOrderConfirmInput>;
export const WORK_ORDER_TASK_STATUSES = [
  'pending',
  'in_progress',
  'completed',
  'canceled',
] as const;

export const workOrderTaskCreateInput = idempotency
  .extend({
    workOrderId: z.string().uuid(),
    name: z.string().trim().min(1).max(200),
    assignedUserId: z.string().uuid().optional(),
    estimatedDurationMinutes: z.number().int().min(1).max(525600).optional(),
  })
  .strict();
export type WorkOrderTaskCreateInput = z.infer<typeof workOrderTaskCreateInput>;
export const workOrderTaskUpdateInput = idempotency
  .extend({
    taskId: z.string().uuid(),
    expectedVersion: z.number().int().positive(),
    name: z.string().trim().min(1).max(200).optional(),
    status: z.enum(WORK_ORDER_TASK_STATUSES).optional(),
    assignedUserId: z.string().uuid().nullable().optional(),
    estimatedDurationMinutes: z.number().int().min(1).max(525600).nullable().optional(),
  })
  .strict();
export type WorkOrderTaskUpdateInput = z.infer<typeof workOrderTaskUpdateInput>;
export const workOrderAssignLineInput = idempotency
  .extend({
    workOrderLineId: z.string().uuid(),
    taskId: z.string().uuid(),
    expectedVersion: z.number().int().positive(),
  })
  .strict();
export type WorkOrderAssignLineInput = z.infer<typeof workOrderAssignLineInput>;
export const workOrderSplitLineInput = idempotency
  .extend({
    workOrderLineId: z.string().uuid(),
    taskId: z.string().uuid(),
    quantityHundredths: z.number().int().positive().max(100000000),
    expectedVersion: z.number().int().positive(),
    reason: z.string().trim().min(1).max(500),
  })
  .strict();
export type WorkOrderSplitLineInput = z.infer<typeof workOrderSplitLineInput>;
export const workOrderAssignVisitInput = idempotency
  .extend({
    taskId: z.string().uuid(),
    visitId: z.string().uuid(),
    expectedVersion: z.number().int().positive(),
    plannedDurationMinutes: z.number().int().min(1).max(525600).optional(),
  })
  .strict();
export type WorkOrderAssignVisitInput = z.infer<typeof workOrderAssignVisitInput>;
export const workOrderUnassignVisitInput = idempotency
  .extend({
    taskId: z.string().uuid(),
    visitId: z.string().uuid(),
    expectedVersion: z.number().int().positive(),
  })
  .strict();
export type WorkOrderUnassignVisitInput = z.infer<typeof workOrderUnassignVisitInput>;
